import { useEffect, useRef, useState } from 'react'
import { Form } from 'react-bootstrap'
import { Pencil } from 'lucide-react'

type DistributorInlineFieldProps = {
  label: string
  value: string
  onChange: (value: string) => void
  type?: 'text' | 'email' | 'url' | 'textarea'
  placeholder?: string
  emptyText?: string
  multiline?: boolean
  rows?: number
  onBlurExtra?: () => void
}

export default function DistributorInlineField({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  emptyText = '—',
  multiline = false,
  rows = 3,
  onBlurExtra,
}: DistributorInlineFieldProps) {
  const [editing, setEditing] = useState(false)
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null)

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus()
      if ('select' in (inputRef.current ?? {})) {
        inputRef.current?.select()
      }
    }
  }, [editing])

  const displayValue = value.trim() || emptyText
  const isEmpty = !value.trim()

  const finishEditing = () => {
    setEditing(false)
    onBlurExtra?.()
  }

  if (editing) {
    return (
      <div className="inline-field inline-field--editing">
        <Form.Label className="inline-field-label">{label}</Form.Label>
        {multiline ? (
          <Form.Control
            ref={inputRef as React.RefObject<HTMLTextAreaElement>}
            as="textarea"
            rows={rows}
            size="sm"
            value={value}
            placeholder={placeholder}
            onChange={(e) => onChange(e.target.value)}
            onBlur={finishEditing}
            onKeyDown={(e) => {
              if (e.key === 'Escape') finishEditing()
            }}
          />
        ) : (
          <Form.Control
            ref={inputRef as React.RefObject<HTMLInputElement>}
            type={type}
            size="sm"
            value={value}
            placeholder={placeholder}
            onChange={(e) => onChange(e.target.value)}
            onBlur={finishEditing}
            onKeyDown={(e) => {
              if (e.key === 'Enter') finishEditing()
              if (e.key === 'Escape') finishEditing()
            }}
          />
        )}
      </div>
    )
  }

  return (
    <button
      type="button"
      className="inline-field"
      onClick={() => setEditing(true)}
      aria-label={`Edit ${label}`}
    >
      <span className="inline-field-label">{label}</span>
      <span className={`inline-field-value${isEmpty ? ' is-empty' : ''}`}>{displayValue}</span>
      <Pencil size={12} className="inline-field-edit-icon" aria-hidden />
    </button>
  )
}
